/**
 * Where to go after signing in, carried through /login as `?next=`.
 *
 * ProtectedRoute used to redirect to a bare /login and the login route always
 * landed on "/", so a link from an email or a bookmark into a show tab was
 * lost at sign-in. Now the destination rides along - but only ever as a path
 * on this site: anything that could leave the origin (an absolute URL, a
 * protocol-relative `//host`, a backslash that browsers read as a slash, a
 * `javascript:` scheme) is dropped, so /login cannot be used as an open
 * redirect.
 */

/** The query parameter /login reads the destination from. */
export const RETURN_PARAM = 'next';

/** The longest destination carried; anything longer is not a real link. */
const MAX_RETURN_LENGTH = 2048;

/**
 * The destination as a same-origin relative path (pathname, search, hash),
 * or null when `raw` is missing, unsafe, or points back at /login.
 */
export const safeReturnPath = (raw: string | null | undefined): string | null => {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > MAX_RETURN_LENGTH) return null;
  // A relative path only: one leading slash, never two, never a backslash or
  // a control character (which URL parsing strips or turns into a slash).
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\\\u0000-\u001f\u007f]/.test(raw)) return null;
  let url: URL;
  try {
    url = new URL(raw, window.location.origin);
  } catch {
    return null;
  }
  if (url.origin !== window.location.origin) return null;
  if (url.pathname === '/login') return null;
  return `${url.pathname}${url.search}${url.hash}`;
};

/**
 * The /login URL for someone signed out at `location`, carrying it as the
 * destination unless it is just the dashboard.
 */
export const loginPathFor = (location: { pathname: string; search?: string; hash?: string }): string => {
  const destination = `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`;
  const safe = safeReturnPath(destination);
  if (!safe || safe === '/') return '/login';
  return `/login?${new URLSearchParams({ [RETURN_PARAM]: safe }).toString()}`;
};
